import {
  AudioConference,
  RoomAudioRenderer,
  useParticipants,
  useLocalParticipant,
  useIsSpeaking,
  useRoomContext,
} from "@livekit/components-react";
import { Participant } from "livekit-client";

function ParticipantRow({ p, customerName }: { p: Participant; customerName?: string }) {
  const speaking = useIsSpeaking(p);
  let name = p.name || p.identity;
  
  // Replace generic/bot names with customer name
  if (name.includes('WhatsApp Bot') || 
      name.includes('Bot') || 
      name.includes('whatsapp') ||
      (name.includes('user_') && /^user_\d+$/.test(name))) {
    name = customerName || 'Customer';
  }

  return (
    <div className={`flex items-center justify-between px-4 py-3 rounded-xl border
      ${speaking ? "border-green-500 bg-white/10" : "border-white/10 bg-white/5"}`}>
      <div className="flex items-center gap-3">
        <div className={`w-2.5 h-2.5 rounded-full ${speaking ? "bg-green-500 animate-pulse" : "bg-gray-400"}`} />
        <div className="text-white font-medium">{name}</div>
      </div>
      <div className="text-white/60 text-sm">
        {p.isMicrophoneEnabled ? "Mic on" : "Muted"}
      </div>
    </div>
  );
}

export function SimpleCallInterface({ onDisconnect, customerName }: { onDisconnect?: () => void; customerName?: string }) {
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const room = useRoomContext();

  const remote = participants.filter(p => p.identity !== localParticipant?.identity);

  const handleDisconnect = async () => {
    try {
      if (room) {
        await room.disconnect();
      }
      onDisconnect?.();
    } catch (error) {
      console.error("Error disconnecting:", error);
      onDisconnect?.();
    }
  };

  const toggleMute = async () => {
    if (localParticipant) {
      const micEnabled = localParticipant.isMicrophoneEnabled;
      await localParticipant.setMicrophoneEnabled(!micEnabled);
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#0f1115] text-white">
      <RoomAudioRenderer />

      {/* Top bar (Meet-ish) */}
      <div className="px-5 py-4 flex items-center justify-between border-b border-white/10">
        <div>
          <div className="text-lg font-semibold">Call</div>
          <div className="text-sm text-white/60">
            {remote.length ? `${remote.length} participant${remote.length > 1 ? "s" : ""}` : "Waiting…"}
          </div>
        </div>
        <div className="text-sm text-white/60">{customerName || "Customer"}</div>
      </div>

      {/* Center: audio stage + participant list */}
      <div className="flex-1 overflow-auto p-5">
        {/* Option A: prefab audio UI */}
        <div className="hidden">
          <AudioConference />
        </div>

        {/* Option B (recommended): custom Meet-like list */}
        <div className="space-y-3 max-w-2xl mx-auto">
          {remote.length === 0 ? (
            <div className="rounded-2xl bg-white/5 border border-white/10 p-6 text-center text-white/70">
              No one joined yet
            </div>
          ) : (
            remote.map(p => <ParticipantRow key={p.identity} p={p} customerName={customerName} />)
          )}
        </div>
      </div>

      {/* Bottom floating controls like Meet */}
      <div className="pb-6 flex justify-center">
        <div className="bg-black/60 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 flex items-center gap-3">
          {/* Mute/Unmute button */}
          <button
            onClick={toggleMute}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              localParticipant?.isMicrophoneEnabled
                ? 'bg-white/20 hover:bg-white/30'
                : 'bg-red-500 hover:bg-red-600'
            }`}
            title={localParticipant?.isMicrophoneEnabled ? "Mute" : "Unmute"}
          >
            {localParticipant?.isMicrophoneEnabled ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
            )}
          </button>


          {/* Disconnect button */}
          <button
            onClick={handleDisconnect}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors flex items-center gap-2"
            title="End Call"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            End Call
          </button>
        </div>
      </div>
    </div>
  );
}
